'use client';
import styled from '@emotion/styled';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { useEventLogging } from '@innovamat/event-logging';

import type { ToolCardItem } from '@innovamat/glimmer-components';
import { ToolCard, Typography } from '@innovamat/glimmer-components';
import { useSchoolCapabilities } from '../../../classrooms-management/hooks/use-school-capabilities';
import { APP_PATHS } from '../../../navigation';

export const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

export const ToolsContainer = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr 1fr;
  gap: 1rem;

  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    grid-template-columns: 1fr 1fr;
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.sm}) {
    grid-template-columns: 1fr;
  }
`;

type ToolOptions = ToolCardItem & {
  hide?: () => boolean;
};

type Props = {
  showFamilyPayments?: boolean;
};

type ShortCutEvents = 'em' | 'training' | 'courses' | 'app';

export function Shortcuts({ showFamilyPayments = false }: Props): JSX.Element {
  const {
    contents: { canNavigateByGrade, canPerformDigitalPractice },
  } = useSchoolCapabilities();
  const { t } = useTranslation();
  const { setEventData } = useEventLogging();

  const navigate = useNavigate();

  const handleSendEvent = (link: ShortCutEvents) => {
    setEventData('launcher_shortcut', { link });
  };

  const openInNewTab = (url: string): void => {
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  const tools: ToolOptions[] = [
    {
      icon: 'TrainingIcon',
      dataTestId: 'training-tool',
      title: t('shortcuts.training-tool.title'),
      description: t('shortcuts.training-tool.description'),
      color: 'blue',
      onClick: () => {
        handleSendEvent('training');
        navigate(
          `${APP_PATHS.COURSE_SELECTOR_BASE_URL}${APP_PATHS.LEARNING_URL}`
        );
      },
      hide: () => false,
    },
    {
      icon: 'CatalogIcon',
      dataTestId: 'catalog-tool',
      title: t('shortcuts.catalog-tool.title'),
      description: t('shortcuts.catalog-tool.description'),
      color: 'purple',
      onClick: () => {
        handleSendEvent('courses');
        navigate(
          `${APP_PATHS.COURSE_SELECTOR_BASE_URL}${APP_PATHS.COURSE_URL}`
        );
      },
      hide: () => !canNavigateByGrade,
    },
    {
      icon: 'AtlasAppIcon',
      dataTestId: 'app-tool',
      title: t('shortcuts.app-tool.title'),
      description: t('shortcuts.app-tool.description'),
      color: 'orange',
      rightIcon: 'OpenNewTabIcon',
      isDisabled: !canPerformDigitalPractice,
      onClick: () => {
        handleSendEvent('app');
        openInNewTab(t('shortcuts.app-tool.url'));
      },
      hide: () => false,
    },
    {
      icon: 'ManipulativeEnvironmentIcon',
      dataTestId: 'manipulative-env-tool',
      title: t('shortcuts.manipulative-env-tool.title'),
      description: t('shortcuts.manipulative-env-tool.description'),
      color: 'teal',
      onClick: () => {
        handleSendEvent('em');
        openInNewTab(`${APP_PATHS.MANIPULATIVE_URL}`);
      },
      hide: () => false,
    },
    {
      icon: 'OrdersIcon',
      dataTestId: 'family-payments-tool',
      title: t('shortcuts.family-payments.title'),
      description: t('shortcuts.family-payments.description'),
      inlineManualId: 'family_payments_shortcut',
      color: 'teal',
      onClick: () => {
        navigate(`${APP_PATHS.FAMILY_PAYMENTS_URL}`);
      },
      hide: () => !showFamilyPayments,
    },
  ];

  return (
    <Container>
      <Typography.H3>{t('shortcuts.title')}</Typography.H3>
      <ToolsContainer data-testid="shortcuts-container">
        {tools
          .filter((tool) => !tool.hide?.())
          .map((tool, id) => (
            <ToolCard key={id} {...tool} />
          ))}
      </ToolsContainer>
    </Container>
  );
}
